"use client"

import { useParams } from "next/navigation"
import Image from "next/image"
import { ArrowRight, Check, Globe, GraduationCap, Building, Landmark, Wallet, MapPin, Book, Users, Calendar, Sparkles, Star, HelpCircle, Home, ClipboardCheck, MessageCircle, ChevronRight } from "lucide-react"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { Section } from "@/components/ui/section"
import { Container } from "@/components/ui/container"
import { Grid } from "@/components/ui/grid"
import { Heading } from "@/components/ui/heading"
import { Paragraph } from "@/components/ui/paragraph"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Badge } from "@/components/ui/badge"
import { UniversityCard } from "@/components/ui/university-card"
import { CTASection } from "@/components/ui/cta-section"
import { Breadcrumbs } from "@/components/breadcrumbs"
import { useCountry } from "@/hooks/use-countries"
import { DESTINATIONS } from "@/constants/images"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { useUniversities, useUniversitiesByCountry } from "@/hooks/use-universities"
import { Card } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { Database } from '@/types/supabase'
import { UniversityCard as UniversityCardType, University, UniversityProgram } from '@/types/university'
import { useState, useEffect, Suspense } from "react"
import LoadingDestination from "./loading"

// Country type from Supabase
type Country = Database['public']['Tables']['countries']['Row'];

export default function DestinationPage() {
  const params = useParams()
  const slug = params?.slug as string
  
  // Fetch country data
  const { data: destination, isLoading, isError } = useCountry(slug)

  // Show error only if we have an explicit error and we're not loading
  if (isError && !isLoading) {
    return (
      <Section>
        <Container>
          <div className="flex flex-col items-center justify-center min-h-[50vh] text-center">
            <Heading level={1}>Destination Not Found</Heading>
            <Paragraph size="lg" className="mt-4 mb-8">
              We couldn't find information about this destination.
            </Paragraph>
            <Button asChild>
              <Link href="/destinations">
                Browse All Destinations
              </Link>
            </Button>
          </div>
        </Container>
      </Section>
    )
  }

  // If we're loading or don't have data yet, show loading skeleton
  if (isLoading || !destination) {
    return <LoadingDestination />
  }

  // Get country flag emoji
  const countryFlagEmojis: Record<string, string> = {
    "ireland": "🇮🇪",
    "poland": "🇵🇱",
    "canada": "🇨🇦",
    "germany": "🇩🇪",
    "australia": "🇦🇺",
    "usa": "🇺🇸",
    "netherlands": "🇳🇱",
    "japan": "🇯🇵",
    "singapore": "🇸🇬",
  }
  
  const flagEmoji = countryFlagEmojis[slug] || ""
  const heroImageUrl = DESTINATIONS[slug.toUpperCase() as keyof typeof DESTINATIONS]

  return (
    <>
      <div className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        {/* Hero Background Image */}
        <div className="absolute inset-0 z-0">
          <Image
            src={`${heroImageUrl}.webp`}
            alt={`${destination.name} landscape`}
            fill
            className="object-cover"
            priority
          />
          {/* Enhanced vignette effect for better text contrast */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70" />
        </div>

        {/* Hero Content with Glassmorphism */}
        <div className="relative z-10 w-full px-4 md:px-6">
          <div className="backdrop-blur-sm bg-black/5 dark:bg-white/5 rounded-xl border border-white/10 shadow-2xl p-8 md:p-12 max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <span className="text-4xl">{flagEmoji}</span>
              <Heading level={1} className="text-white dark:text-white">Study in {destination.name}</Heading>
            </div>
            
            <Paragraph size="lg" className="text-white/90 dark:text-white/90 mt-6 text-lg">
              {destination.description}
            </Paragraph>
            
            {destination.tags && destination.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-8">
                {destination.tags.map((tag: string) => (
                  <Badge 
                    key={tag} 
                    variant="outline" 
                    className="bg-white/5 dark:bg-black/20 text-white border-white/20 backdrop-blur-sm"
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
            
            <div className="mt-8 flex flex-wrap gap-4">
              <Button 
                asChild 
                size="lg" 
                variant="default" 
                className="bg-white text-black hover:bg-white/90 dark:bg-white dark:text-black dark:hover:bg-white/90"
              >
                <Link href="#universities">
                  Explore Universities <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button 
                variant="outline" 
                asChild 
                size="lg" 
                className="border-white/20 text-black dark:text-white hover:bg-white/10 dark:border-white dark:hover:bg-white/20"
              >
                <Link href="#requirements">
                  Admission Requirements
                </Link>
              </Button>
              
              {/* Country-specific website links */}
              {slug === "poland" && (
                <Button 
                  variant="default" 
                  asChild 
                  size="lg" 
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  <Link href="https://studyinpoland.app" target="_blank" rel="noopener noreferrer">
                    Visit StudyInPoland.app <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Key Facts Section */}
      <Section background="muted">
        <Container>
          <div className="flex flex-col md:flex-row gap-8 md:gap-12 lg:gap-16">
            {/* Left column - Key Facts */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-6">
                <Globe className="h-5 w-5 text-primary" />
                <h2 className="text-2xl font-bold">Key Facts</h2>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="flex gap-3">
                  <MapPin className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium">Capital</h3>
                    <p className="text-muted-foreground">{destination.capital || "Dublin"}</p>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <Users className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium">Population</h3>
                    <p className="text-muted-foreground">{destination.population || "5 million"}</p>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <Globe className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium">Language</h3>
                    <p className="text-muted-foreground">{destination.language || "English"}</p>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <Wallet className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium">Currency</h3>
                    <p className="text-muted-foreground">{destination.currency || "Euro (€)"}</p>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <Calendar className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium">Academic Year</h3>
                    <p className="text-muted-foreground">{destination.academicYear || "September to May"}</p>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <Book className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium">Education System</h3>
                    <p className="text-muted-foreground">{destination.educationSystem || "European"}</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right column - Statistics */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-6">
                <GraduationCap className="h-5 w-5 text-primary" />
                <h2 className="text-2xl font-bold">Education Statistics</h2>
              </div>
              
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between mb-2">
                    <h3 className="font-medium">International Students</h3>
                    <span className="text-primary font-medium">{destination.internationalStudents || "35,000+"}</span>
                  </div>
                  <div className="w-full bg-muted-foreground/20 rounded-full h-2.5">
                    <div className="bg-primary h-2.5 rounded-full" style={{ width: "65%" }}></div>
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between mb-2">
                    <h3 className="font-medium">Top Universities</h3>
                    <span className="text-primary font-medium">{destination.universitiesCount || "8+"}</span>
                  </div>
                  <div className="w-full bg-muted-foreground/20 rounded-full h-2.5">
                    <div className="bg-primary h-2.5 rounded-full" style={{ width: "80%" }}></div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <h3 className="font-medium">Employability Rate</h3>
                    <span className="text-primary font-medium">{destination.employabilityRate || "High"}</span>
                  </div>
                  <div className="w-full bg-muted-foreground/20 rounded-full h-2.5">
                    <div className="bg-primary h-2.5 rounded-full" style={{ width: "75%" }}></div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <h3 className="font-medium">Student Satisfaction</h3>
                    <span className="text-primary font-medium">{destination.studentSatisfaction || "Excellent"}</span>
                  </div>
                  <div className="w-full bg-muted-foreground/20 rounded-full h-2.5">
                    <div className="bg-primary h-2.5 rounded-full" style={{ width: "90%" }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      {/* Universities Section */}
      <Section id="universities">
        <Container>
          <div className="flex items-center gap-2 mb-4">
            <Building className="h-5 w-5 text-primary" />
            <Heading level={2} className="text-2xl font-bold">Universities in {destination.name}</Heading>
          </div>
          <Paragraph size="lg" className="text-muted-foreground mb-8 max-w-3xl">
            Explore top universities in {destination.name}. 
            We partner with institutions offering a wide range of programs and excellent student support. 
            Many of these universities have specific programs tailored for international students.
          </Paragraph>

          <Suspense fallback={<UniversitiesLoadingSkeleton />}>
            <UniversitiesByCountry country={destination} />
          </Suspense>
        </Container>
      </Section>
      
      {/* Requirements Section */}
      <Section id="requirements" background="muted">
        <Container>
          <div className="flex items-center gap-2 mb-4">
            <ClipboardCheck className="h-5 w-5 text-primary" />
            <Heading level={2} className="text-2xl font-bold">Admission Requirements</Heading>
          </div>
          <Paragraph size="lg" className="text-muted-foreground mb-8 max-w-3xl">
            General admission requirements for studying in {destination.name}. 
            Specific requirements may vary by university and program.
          </Paragraph>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Academic Requirements */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <GraduationCap className="h-6 w-6 text-primary" />
                <Heading level={3} className="text-xl font-semibold">Academic Requirements</Heading>
              </div>
              <ul className="space-y-3">
                {(destination.academicRequirements || [
                  "Completed secondary education (high school diploma or equivalent).",
                  "Minimum GPA or equivalent academic standing (varies by institution).",
                  "Specific subject prerequisites may apply for certain programs (e.g., math for engineering).",
                  "Official academic transcripts and certificates (often requiring certified translations)."
                ]).map((req: string, index: number) => (
                  <li key={index} className="flex gap-3">
                    <Check className="h-5 w-5 text-green-500 shrink-0 mt-1" />
                    <span>{req}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Language Proficiency */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <MessageCircle className="h-6 w-6 text-primary" />
                <Heading level={3} className="text-xl font-semibold">Language Proficiency</Heading>
              </div>
              <ul className="space-y-3">
                {(destination.languageRequirements || [
                  `Proof of ${destination.language || "English"} proficiency (e.g., IELTS, TOEFL, PTE Academic).`,
                  `Minimum scores vary by university and program. Some universities offer pathway programs if you don't meet direct entry scores.`, 
                  `Some programs might be taught in ${destination.language || "English"}, but local language skills are beneficial for daily life.`
                ]).map((req: string, index: number) => (
                  <li key={index} className="flex gap-3">
                    <Check className="h-5 w-5 text-green-500 shrink-0 mt-1" />
                    <span>{req}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Visa & Other */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4 mt-6">
                <Landmark className="h-6 w-6 text-primary" />
                <Heading level={3} className="text-xl font-semibold">Visa & Other Documents</Heading>
              </div>
              <ul className="space-y-3">
                {(destination.visaRequirements || [
                  "Valid passport with sufficient validity.",
                  "Student visa or study permit (requirements depend on your nationality and duration of study).",
                  "Proof of financial means to cover tuition fees and living expenses.",
                  "Letter of acceptance from a recognized educational institution.",
                  "Health insurance coverage.",
                  "Sometimes, a medical examination or police clearance certificate may be required."
                ]).map((req: string, index: number) => (
                  <li key={index} className="flex gap-3">
                    <Check className="h-5 w-5 text-green-500 shrink-0 mt-1" />
                    <span>{req}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Container>
      </Section>

      {/* Why Study Section */}
      <Section>
        <Container>
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="h-5 w-5 text-primary" />
            <Heading level={2} className="text-2xl font-bold">Why Study in {destination.name}?</Heading>
          </div>
          <Paragraph size="lg" className="text-muted-foreground mb-8 max-w-3xl">
            Discover the unique advantages of choosing {destination.name} as your study destination.
          </Paragraph>
          <Grid cols={{ default: 1, md: 2, lg: 3 }} gap="md">
            {(destination.whyStudy || [
              {
                title: "World-Class Education",
                description: "Access high-quality education with globally recognized degrees and research opportunities.",
                icon: "GraduationCap"
              },
              {
                title: "Cultural Richness",
                description: `Experience a vibrant culture, historic landmarks, and a welcoming environment for international students. (${destination.name} specific detail can be added here).`,
                icon: "Globe"
              },
              {
                title: "Career Opportunities",
                description: "Benefit from strong industry links, internships, and post-study work options in a thriving economy.",
                icon: "Building"
              },
              {
                title: "Affordable Living",
                description: `Compared to many Western European countries, the cost of living and tuition can be more manageable in ${destination.name}. (If applicable, otherwise generalize).`,
                icon: "Wallet"
              },
              {
                title: "Student Life & Support",
                description: "Enjoy a dynamic student life with numerous clubs, societies, and comprehensive support services for internationals.",
                icon: "Users"
              },
              {
                title: "Gateway to Europe/Region",
                description: `Located centrally, ${destination.name} offers easy travel to explore neighboring countries and cultures. (If applicable).`,
                icon: "MapPin"
              }
            ]).map((reason: any, index: number) => {
              let IconComponent: React.ElementType = Star // Default icon
              if (reason.icon) {
                const icons: { [key: string]: React.ElementType } = { Globe, GraduationCap, Building, Wallet, Users, MapPin, Sparkles, Check, Star, HelpCircle, Home }
                IconComponent = icons[reason.icon] || Star
              }
              return (
                <Card key={index} className="p-6 flex flex-col items-start">
                  <div className="p-3 rounded-full bg-primary/10 mb-4">
                    <IconComponent className="h-6 w-6 text-primary" />
                  </div>
                  <Heading level={4} className="text-lg font-semibold mb-2">{reason.title}</Heading>
                  <Paragraph size="sm" className="text-muted-foreground">{reason.description}</Paragraph>
                </Card>
              )
            })}
          </Grid>
        </Container>
      </Section>

      {/* Student Testimonials Section */}
      {destination.testimonials && destination.testimonials.length > 0 && (
        <Section background="muted">
          <Container>
            <div className="flex items-center gap-2 mb-4">
              <Star className="h-5 w-5 text-primary" />
              <Heading level={2} className="text-2xl font-bold">Student Stories from {destination.name}</Heading>
            </div>
            <Paragraph size="lg" className="text-muted-foreground mb-8 max-w-3xl">
              Hear from students who have experienced studying in {destination.name} firsthand.
            </Paragraph>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {destination.testimonials.map((testimonial: any, index: number) => (
                <Card key={index} className="p-6 flex flex-col">
                  <div className="flex items-center mb-4">
                    {testimonial.avatar && (
                      <Image 
                        src={testimonial.avatar}
                        alt={testimonial.name} 
                        width={48} 
                        height={48} 
                        className="rounded-full mr-4"
                      />
                    )}
                    <div>
                      <Heading level={4} className="text-md font-semibold">{testimonial.name}</Heading>
                      <Paragraph size="sm" className="text-muted-foreground">{testimonial.program}, {testimonial.university}</Paragraph>
                    </div>
                  </div>
                  <blockquote className="text-muted-foreground italic border-l-2 border-primary pl-4">
                    {testimonial.quote}
                  </blockquote>
                  {testimonial.fullStoryLink && (
                    <Button variant="link" asChild className="mt-4 self-start px-0">
                      <Link href={testimonial.fullStoryLink}>
                        Read full story <ChevronRight className="ml-1 h-4 w-4"/>
                      </Link>
                    </Button>
                  )}
                </Card>
              ))}
            </div>
          </Container>
        </Section>
      )}

      {/* Practical Information Section */}
      <Section>
        <Container>
          <div className="flex items-center gap-2 mb-4">
            <HelpCircle className="h-5 w-5 text-primary" />
            <Heading level={2} className="text-2xl font-bold">Practical Information</Heading>
          </div>
          <Paragraph size="lg" className="text-muted-foreground mb-8 max-w-3xl">
            Essential tips and information for planning your studies in {destination.name}.
          </Paragraph>

          <Accordion type="single" collapsible className="w-full">
            {(destination.practicalInfo || [
              {
                title: "Accommodation Options",
                content: "Information about university dormitories, private apartments, shared housing, and typical costs. Tips on finding accommodation."
              },
              {
                title: "Cost of Living",
                content: "Breakdown of average monthly expenses: accommodation, food, transportation, utilities, personal expenses. Comparison with other major cities."
              },
              {
                title: "Student Visa Process",
                content: "Step-by-step guide on applying for a student visa for ${destination.name}: required documents, application timeline, and where to apply."
              },
              {
                title: "Health Insurance",
                content: "Importance of health insurance, options available for international students, and how to obtain coverage."
              },
              {
                title: "Banking & Finances",
                content: "How to open a bank account in ${destination.name}, manage finances, and information about scholarships or financial aid available."
              },
              {
                title: "Getting Around",
                content: "Overview of public transportation (buses, trams, trains), student discounts, and tips for navigating the city/country."
              }
            ]).map((item: any, index: number) => (
              <AccordionItem value={`item-${index}`} key={index}>
                <AccordionTrigger className="text-lg hover:no-underline">
                  <div className="flex items-center gap-3">
                     {/* Could add icons here based on item.title */}
                    {item.title}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-base text-muted-foreground pt-2 pb-4">
                  {item.content.replace(/\$\{destination.name\}/g, destination.name)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Container>
      </Section>

      {/* Call to Action */}
      <CTASection 
        title={`Ready to Study in ${destination.name}?`}
        description="Let us help you find the perfect university and program to kickstart your academic journey."
        action={{ href: "/apply", text: "Apply Now" }}
      />
    </>
  )
}

function UniversitiesLoadingSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {[...Array(3)].map((_, i) => (
        <Card key={i} className="p-4 space-y-3">
          <Skeleton className="h-40 w-full rounded-md bg-muted" />
          <Skeleton className="h-6 w-3/4 bg-muted" />
          <Skeleton className="h-4 w-1/2 bg-muted" />
          <div className="flex justify-between items-center pt-2">
            <Skeleton className="h-5 w-1/3 bg-muted" />
            <Skeleton className="h-8 w-1/4 bg-muted rounded-md" />
          </div>
        </Card>
      ))}
    </div>
  )
}


interface UniversitiesByCountryProps {
  country: Country;
}

function UniversitiesByCountry({ country }: UniversitiesByCountryProps) {
  const { data: universities, isLoading, error } = useUniversitiesByCountry(country.id);

  if (isLoading) return <UniversitiesLoadingSkeleton />;
  if (error) return <Paragraph size="sm">Error loading universities: {error.message}</Paragraph>;
  if (!universities || universities.length === 0) {
    return <Paragraph size="sm">No universities found for {country.name} at the moment. Please check back later.</Paragraph>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {universities.slice(0, 3).map((uni: University) => { 
        const cardData: UniversityCardType = {
          id: uni.id,
          name: uni.name,
          slug: uni.slug,
          countryId: uni.countryId,
          countryName: uni.countryName, 
          logo: uni.logo,
          isPublic: uni.isPublic,
          ranking: {
            qs: uni.ranking?.qs,
          },
          location: uni.location,
          featuredFields: uni.featuredPrograms?.map((p: UniversityProgram) => p.field).slice(0, 3) || [], 
          qogentSuccessRate: uni.qogentMetrics?.admissionSuccessRate || "N/A",
        };
        return (
          <UniversityCard 
            key={cardData.id} 
            university={cardData} 
          />
        );
      })}
    </div>
  );
} 